import {Component, Element, Event, EventEmitter, getAssetPath, h, Host, Fragment, Prop, State, Watch} from '@stencil/core';
import {ImageLoadingStatus, SliderImageInfo, THEME} from '../../globals/typings';
import {CarouselImage} from '../marketing-cc/ConfigData';
import { TranslationService } from '../../services/translation-service';

@Component({
  tag: 'mcc-image-slider',
  styleUrl: 'image-slider.scss',
  shadow: true,
  assetsDirs: ['assets/AUDI23', 'assets/CUPRA24', 'assets/SEAT24', 'assets/SKODA23', 'assets/VW22', 'assets/default'],
})
export class ImageSlider {

  @Event({
    eventName: 'mccImageLoadError',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) imageLoadError: EventEmitter<void>;

  /**
   * An array of CarouselImages to fill the slider with.
   */
  @Prop() carouselImages: CarouselImage[];
  @Prop() theme: THEME;

  @Element()
  private host: HTMLElement;

  private slider: HTMLDivElement;
  private slides: HTMLDivElement[] = [];

  private showFooter: boolean = true;

  @State()
  private imageInfos: SliderImageInfo[];
  get loadedImages() {
    return this.imageInfos.filter(imageInfo => imageInfo.loadingStatus === ImageLoadingStatus.success);
  }
  get goodImages() {
    return this.imageInfos.filter(imageInfo => imageInfo.loadingStatus !== ImageLoadingStatus.error);
  }

  private readonly translationService = TranslationService;
  private slidesResizeObserver: ResizeObserver;
  private currentSlideWidth;
  private isDragging = false;
  private startPos = 0;
  private currentTranslate = 0;
  private prevTranslate = 0;
  private animationID = 0;
  private currentIndex = 0;

  componentWillLoad() {
    this.watchCarouselImagesHandler(this.carouselImages);
  }

  componentDidRender() {
    // get references to necessary HTML elements of the slider
    this.slider = this.host.shadowRoot.querySelector('.slides-container');
    this.slides = Array.from(this.host.shadowRoot.querySelectorAll('.slide'));

    // set initial slide width
    this.currentSlideWidth = this.slides[0] ? this.slides[0].clientWidth : null;

    // creating an observer that adjusts the slide width whenever the sliderWindow size changes
    const sliderWindow = this.host.shadowRoot.querySelector('.slider-window');
    this.slidesResizeObserver = new ResizeObserver((entries) => this.resizeSlides(entries));
    this.slidesResizeObserver.observe(sliderWindow);

    // disable some default behavior
    this.slider.addEventListener('dragstart', (e) => e.preventDefault());
    this.slider.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      event.stopPropagation();
      return false;
    });

    // Touch Events
    this.slider.addEventListener('touchstart', (e) => this.touchStart(e), {passive: true});
    this.slider.addEventListener('touchend', () => this.touchEnd());
    this.slider.addEventListener('touchmove', (e) => this.touchMove(e), {passive: true});

    // Mouse Events
    this.slider.addEventListener('mousedown', (e) => this.touchStart(e));
    this.slider.addEventListener('mouseup', () => this.touchEnd());
    this.slider.addEventListener('mouseleave', () => this.touchEnd());
    this.slider.addEventListener('mousemove', (e) => this.touchMove(e));

    const root = this.host.shadowRoot.querySelector('#nwi-root');
    if (!this.showFooter) {
      root.classList.add('no-footer');
    } else {
      root.classList.remove('no-footer');
    }
  }

  disconnectedCallback() {
    this.slidesResizeObserver.disconnect();
  }

  @Watch('theme')
  watchThemeHandler() {
    this.currentIndex = 0;
  }

  @Watch('carouselImages')
  watchCarouselImagesHandler(newCarouselImages: CarouselImage[]) {
    if (this.currentIndex > newCarouselImages.length) {
      this.currentIndex = 0;
    }

    this.imageInfos = new Array<SliderImageInfo>();
    let i = 0;
    for (let carouselImage of newCarouselImages) {
      const imageInfo = new SliderImageInfo(carouselImage);
      imageInfo.loadingStatus = ImageLoadingStatus.loading;
      imageInfo.originalIndex = i;
      imageInfo.cssClasses.add('slider-image');
      this.imageInfos.push(imageInfo);
      i++;
    }
  }

  resizeSlides(entries: ResizeObserverEntry[]) {
    for (let entry of entries) {
      for (let slide of this.slides) {
        this.slider.classList.add('no-transition');
        // this must come after no-transition class is set in order to trigger a reflow
        this.currentSlideWidth = entry.target.clientWidth;
        slide.style.width = `${this.currentSlideWidth}px`;
        this.setPositionByIndex();
        this.slider.classList.remove('no-transition');
      }
      this.slider.style.width = this.currentSlideWidth ? `${this.currentSlideWidth * (this.imageInfos.length || 1)}px` : '';
    }
  }

  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }

  touchStart(event: any) {
    if (this.goodImages.length <= 1) {
      return;
    }
    this.startPos = this.getPositionX(event);
    this.isDragging = true;
    this.animationID = requestAnimationFrame(() => this.animation());
    this.slider.classList.add('grabbing');
  }

  touchMove(event: any) {
    if (this.goodImages.length <= 1) {
      return;
    }
    if (this.isDragging) {
      const currentPosition = this.getPositionX(event);
      this.currentTranslate = this.prevTranslate + currentPosition - this.startPos;
    }
  }

  touchEnd() {
    if (this.goodImages.length <= 1) {
      return;
    }
    cancelAnimationFrame(this.animationID);
    this.isDragging = false;
    const movedBy = this.currentTranslate - this.prevTranslate;

    // if moved enough negative then snap to next slide if there is one
    if (movedBy < -100 && this.currentIndex < this.goodImages.length - 1) {
      this.currentIndex += 1;
    }

    // if moved enough positive then snap to previous slide if there is one
    if (movedBy > 100 && this.currentIndex > 0) {
      this.currentIndex -= 1;
    }

    this.setPositionByIndex();

    this.slider.classList.remove('grabbing');
  }

  animation() {
    this.setSliderPosition();
    if (this.isDragging) {
      requestAnimationFrame(() => this.animation());
    }
  }

  setPositionByIndex() {
    this.currentTranslate = this.currentIndex * -this.currentSlideWidth;
    this.prevTranslate = this.currentTranslate;
    this.setSliderPosition();
  }

  setSliderPosition() {
    this.slider.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  prevSlide() {
    if (this.currentIndex >= 0) {
      this.currentIndex--;
      if (this.currentIndex < 0) this.currentIndex = this.goodImages.length - 1;
      this.setPositionByIndex();
    }
  }

  nextSlide() {
    if (this.currentIndex <= this.goodImages.length - 1) {
      this.currentIndex++;
      if (this.currentIndex >= this.goodImages.length) this.currentIndex = 0;
      this.setPositionByIndex();
    }
  }

  onImageLoad(imageInfo: SliderImageInfo, _index: number) {
    // console.log(`image "${imageInfo.src}" (index: ${_index}) loaded!`)
    imageInfo.loadingStatus = ImageLoadingStatus.success;

    this.imageCompleteCallback();
  }

  onImageError(imageInfo: SliderImageInfo, _index: number) {
    // console.error(`image "${imageInfo.src}" (index: ${_index}) failed to load...`)
    imageInfo.loadingStatus = ImageLoadingStatus.error;

    this.imageCompleteCallback();
  }

  imageCompleteCallback() {
    let loadErrorCount = 0;
    this.imageInfos.forEach((image) => {
      if (image.loadingStatus === ImageLoadingStatus.error) {
        loadErrorCount++;
        image.loadIndex = -1;
      } else {
        image.loadIndex = image.originalIndex - loadErrorCount;
      }
    });
    if (this.allImagesLoaded() && loadErrorCount === this.imageInfos.length) {
      this.imageLoadError.emit();
    }
    this.showFooter = this.goodImages.length > 1;
    this.imageInfos = [...this.imageInfos];
  }

  allImagesLoaded() {
    return this.imageInfos.every(info => info.loadingStatus !== ImageLoadingStatus.loading);
  }

  handleImageSliderKeyDown(event: KeyboardEvent) {
    if (['ArrowRight', 'ArrowDown'].includes(event.code)) {
      event.preventDefault();
      this.nextSlide();
    }
    if (['ArrowLeft', 'ArrowUp'].includes(event.code)) {
      event.preventDefault();
      this.prevSlide();
    }
  }

  render() {
    return (
        <Host>
          <div id="nwi-root" class={this.theme}>
            <div class="slider-window cropped-images"
                 tabIndex={0}
                 role="listbox"
                 aria-label={this.translationService.get('mcc.image-selector.text')}
                 onKeyDown={e => this.handleImageSliderKeyDown(e)}>
              <div class={'slides-container' + (this.goodImages.length <= 1 ? ' no-grab' : '')}>
                {this.goodImages.map((imageInfo, i) =>
                  <div class={'slide-wrapper'} key={"slider"}>
                    <div class={{
                      'slide': true,
                      'loading': imageInfo.loadingStatus === ImageLoadingStatus.loading}}>
                      <fading-image src={imageInfo.src}
                           alt={imageInfo.alt}
                           role="option"
                           class={imageInfo.classList}
                           onLoad={() => this.onImageLoad(imageInfo, i)}
                           onError={() => this.onImageError(imageInfo, i)}/>
                    </div>
                  </div>
                )}
                {this.goodImages.length === 0 &&
                  <div class={'slide-wrapper'}>
                    <div class="slide">
                      <img src={getAssetPath(`./assets/default/not-found.svg`)}
                           role="option"
                           alt="car image not found"
                           class="slider-image fallback"
                           key={'fallback-image'}/>
                    </div>
                  </div>
                }
              </div>
              {this.goodImages.length > 1 && <>
                <div class="prev" onClick={() => this.prevSlide()} aria-hidden="true">
                  {this.getIconForCurrentTheme('button-backward', 'prev-icon', this.translationService.get('mcc.prev-icon.text'))}
                </div>
                <div class="next" onClick={() => this.nextSlide()} aria-hidden="true">
                  {this.getIconForCurrentTheme('button-forward', 'next-icon', this.translationService.get('mcc.next-icon.text'))}
                </div>
              </>}
            </div>
          </div>
        </Host>
    )
  }

  getIconForCurrentTheme(iconName: string, className: string, alt: string) {
    const theme = this.theme === THEME.LNF22 ? THEME.VW22 : this.theme;
    return <img src={getAssetPath(`./assets/${theme}/${iconName}.svg`)} class={className} alt={alt}/>
  }
}
